import React, { useMemo, useEffect } from 'react';
import { CssBaseline, StyledEngineProvider, ThemeProvider, GlobalStyles } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { Provider } from 'react-redux';
import { SnackbarProvider, useSnackbar, SnackbarKey } from 'notistack';
import { Button } from '@mui/material';

import 'swiper/css';
import 'swiper/css/free-mode';
import 'swiper/css/scrollbar';
import 'swiper/css/navigation';

import Router from './Router';
import { getTheme } from 'theme';
import store from 'store';
import { useThemePreference } from 'hooks';

import 'styles/app.scss';
import 'styles/theme.scss';

const getGlobalStyleVariables = (theme: Theme) => {
  const mode = theme.customMode || theme.palette.mode;
  const isDark = mode === 'dark';
  const isCalm = mode === 'calm';
  const isNature = mode === 'nature';

  return {
    ':root': {
      '--app-brand-primary': isDark ? '#33D0FF' : isCalm ? '#6FA4D9' : isNature ? '#4CAF7D' : '#81CFDD',
      '--app-chat': isDark ? '#FFFFFF' : '#979797',
      '--app-page-bg': isDark ? '#1F2429' : isCalm ? '#EDF3FB' : isNature ? '#F3F8F5' : '#FAFAFA',
      '--app-surface': isDark ? '#2A2F35' : '#FFFFFF',
      '--app-surface-selected': isDark ? '#323840' : isCalm ? '#EAF1FB' : isNature ? '#E8F3EC' : '#FFFFFF',
      '--app-left-menu-selected': isDark ? '#323840' : isCalm ? '#EAF1FB' : isNature ? '#E8F3EC' : '#F3F3F3',
      '--app-queue-surface': isDark ? '#2A2F35' : isCalm ? '#F5F9FE' : isNature ? '#F7FBF8' : '#FFFFFF',
      '--app-queue-header-border': isDark ? '#33D0FF' : theme.palette.primary.main,
      '--app-queue-header-bg': isDark ? 'var(--app-header-bg)' : isCalm ? '#DCE5F0' : isNature ? '#DEEAE1' : '#F3F3F3',
      '--app-patient-list-hover': isDark
        ? 'rgba(255,255,255,0.06)'
        : isCalm
        ? '#E5EEF8'
        : isNature
        ? '#E6F1E8'
        : '#FFFFFF',
      '--app-patient-list-selected': isDark ? '#323840' : isCalm ? '#DFEAF7' : isNature ? '#DFEDE1' : '#FFFFFF',
      '--app-header-bg': isDark ? '#0E1114' : '#FFFFFF',
      '--app-header-shadow': isDark
        ? '0 4px 8px rgba(0,0,0,0.5)'
        : isCalm
        ? '0 4px 14px rgba(111, 164, 217, 0.14)'
        : isNature
        ? '0 4px 14px rgba(76, 175, 125, 0.12)'
        : '0 4px 5px rgba(0,0,0,0.08)',
      '--app-text-default': isDark ? '#FFFFFF' : '#2F3A4A',
      '--app-text-muted': isDark ? 'rgba(255,255,255,0.7)' : isCalm ? '#6C778C' : isNature ? '#6F8576' : '#626363',
      '--app-card-shadow': isDark
        ? '0 2px 8px rgba(0,0,0,0.4)'
        : isCalm
        ? '0 6px 18px rgba(111, 164, 217, 0.12)'
        : isNature
        ? '0 6px 18px rgba(76, 175, 125, 0.10)'
        : '0 2px 8px rgba(0,0,0,0.05)',
      '--app-card-outline': isDark ? 'rgba(255,255,255,0.22)' : isNature ? '#DDE9DF' : '#E3EAF3',
      '--app-field-surface': isDark ? '#3A4149' : '#FFFFFF',
      '--app-field-surface-disabled': isDark ? '#2F353B' : isCalm ? '#E4EDF7' : isNature ? '#E6F0E8' : '#F3F3F3',
      '--app-button-disabled-bg': isDark ? '#979797' : isCalm ? '#A6B9CF' : isNature ? '#9DB8A7' : '#979797',
      '--app-button-disabled-text': '#FFFFFF',
      '--app-field-outline': isDark ? 'transparent' : isNature ? '#D4E2D6' : '#D6DEE8',
      '--app-field-divider': isDark ? 'rgba(255,255,255,0.08)' : isNature ? '#E0E9E1' : '#E6EDF5',
      '--app-field-header-divider': isDark ? 'rgba(255,255,255,0.14)' : 'rgba(0,0,0,0.23)',
      '--app-row-hover': isDark ? 'rgba(255,255,255,0.06)' : isCalm ? '#E8F0FB' : isNature ? '#EAF5EE' : '#F9F9F9',
      '--app-tabs-bg': isDark ? '#171B20' : isCalm ? '#E7EEF8' : isNature ? '#E8F3EC' : '#F3F3F3',
      '--app-tab-active': isCalm ? '#6FA4D9' : isNature ? '#4CAF7D' : theme.palette.primary.main,
      '--app-tab-text': isDark ? 'rgba(255,255,255,0.7)' : isCalm ? '#6C778C' : isNature ? '#6F8576' : '#626363',
      '--app-tab-text-active': theme.palette.primary.main,
      '--app-tab-indicator': isCalm ? '#6FA4D9' : isNature ? '#4CAF7D' : theme.palette.primary.main,
      '--app-videopreview': isDark ? 'rgba(255,255,255,0.7)' : isCalm ? '#F2F6FB' : isNature ? '#EDF5EF' : '#F3F3F3',
      '--app-video-muted': isDark ? '#161616' : isCalm ? '#DCE8F6' : isNature ? '#E2EEE4' : '#F3F3F3',
      '--app-welcome-panel-bg': isDark
        ? 'linear-gradient(135deg, #445466 0%, #2E3A49 52%, #22303E 100%)'
        : isCalm
        ? 'linear-gradient(135deg, #F2F7FD 0%, #DCEAF8 48%, #BFD4EA 100%)'
        : isNature
        ? 'linear-gradient(135deg, #F2FAF4 0%, #DBEFE0 48%, #B9DCC4 100%)'
        : 'linear-gradient(135deg, #F4F8FD 0%, #DCE8F4 48%, #B7CCE4 100%)',
      '--app-success': isNature ? '#3FAE6A' : '#2E9E6F',
      '--app-error': theme.palette.error.main,
      '--app-overlay-scrim': isDark ? 'rgba(16, 20, 24, 0.58)' : 'rgba(255, 255, 255, 0.62)',
      '--app-tooltip-overlay': isDark ? 'rgba(16, 20, 24, 0.58)' : 'rgba(18, 22, 28, 0.28)',
      '--swiper-theme-color': theme.palette.primary.main,
      '--app-subscription-banner-healthy': theme.palette.primary.main,
      '--app-subscription-banner-warning': theme.palette.error.main,
      '--app-subscription-banner-critical': theme.palette.error.main,
      '--app-selector-field-outline': isDark ? 'rgba(255,255,255,0.08)' : '#e8e8e8',
      '--app-pagination-bg': isDark ? '#323840' : '#f9f9f9',
      '--app-register-step-number-bg': isDark ? 'rgba(255,255,255,0.28)' : '#979797',
      '--app-register-hero-bg': isDark
        ? 'linear-gradient(138deg, #1B2933 0%, #1E2D32 100%)'
        : isCalm
        ? 'linear-gradient(138deg, #DFF3FF 0%, #D7EEFD 52%, #C7E6F7 100%)'
        : isNature
        ? 'linear-gradient(138deg, #E7F5EA 0%, #DBF0E0 52%, #CBE6D1 100%)'
        : 'linear-gradient(138deg, #def3ff 0%, #dafff8 100%)',
      '--app-register-promo-circle-1': isDark
        ? 'linear-gradient(289deg, #314454 -57.13%, #28484A 68.59%, #2B4D56 199.66%)'
        : 'linear-gradient(289deg, #7E7BFF -57.13%, #7EFFE0 68.59%, var(--app-brand-primary) 199.66%)',
      '--app-register-promo-circle-2': isDark
        ? 'linear-gradient(289deg, #232A33 -57.13%, #213239 68.59%, #243645 199.66%)'
        : 'linear-gradient(289deg, #EADAFF -57.13%, #CCFFFC 68.59%, #D0EEFF 199.66%)',
      '--app-register-promo-border-bg': isDark
        ? 'linear-gradient(289deg, #2A3A45 -57.13%, #2A4142 68.59%, #324658 199.66%)'
        : 'linear-gradient(289deg, #EADAFF -57.13%, #CCFFFC 68.59%, #D0EEFF 199.66%)',
      '--app-register-promo-code-bg': isDark
        ? 'linear-gradient(258.76deg, #2CA3BE 9.08%, #436FB2 173.92%)'
        : 'linear-gradient(258.76deg, var(--app-brand-primary) 9.08%, #79AFFF 173.92%)',
      '--app-video-pane-outline': isDark ? 'rgba(0,0,0,0.42)' : isCalm ? '#D9E4F0' : isNature ? '#DDE8DF' : '#E3EAF3',
      '--app-video-pane-shadow': isDark
        ? '0 0 0 1px rgba(0,0,0,0.35), 0 8px 24px rgba(0,0,0,0.3)'
        : isCalm
        ? '0 1px 4px rgba(111, 164, 217, 0.10), 0 10px 24px rgba(111, 164, 217, 0.08)'
        : isNature
        ? '0 1px 4px rgba(76, 175, 125, 0.10), 0 10px 24px rgba(76, 175, 125, 0.08)'
        : '0 1px 4px rgba(0,0,0,0.06), 0 10px 24px rgba(0,0,0,0.05)',
      '--app-welcome-panel-icon-bg': isDark
        ? 'linear-gradient(145deg, #4A5D73 0%, #36485E 100%)'
        : isCalm
        ? 'linear-gradient(145deg, #DDEAF8 0%, #AFC9E3 100%)'
        : isNature
        ? 'linear-gradient(145deg, #DDEFE1 0%, #A9D0B4 100%)'
        : 'linear-gradient(145deg, #DCE9F6 0%, #AFC9E6 100%)',
      '--app-welcome-panel-title': isDark ? '#e8edf4' : '#393534',
      '--app-welcome-panel-name': isDark ? '#e8edf4' : '#393534',
      '--app-welcome-panel-icon-fg': isDark ? '#c7d3e0' : 'var(--app-text-muted)',
      '--app-tc-action-btn-bg': isDark ? '#2e8b6e' : 'transparent',
      '--app-tc-action-btn-color': isDark ? '#ffffff' : '#3bb8a4',
      '--app-tc-action-btn-border': isDark ? '#3bb8a4' : '#cbe5e0',
      '--app-tc-action-btn-hover-bg': isDark ? '#38a885' : '#f0faf8',
      '--app-tc-search-bg': isDark ? 'var(--app-field-surface)' : '#ffffff',
      '--app-tc-search-border': isDark ? 'rgba(255,255,255,0.15)' : '#d1d1d1',
      '--app-fb-accent-soft': isDark ? 'rgba(59,184,164,0.1)' : '#f0faf8',
      '--app-fb-accent-border': isDark ? 'rgba(59,184,164,0.3)' : '#cbe5e0',
      '--app-builder-page-bg': isDark ? '#182226' : '#eeeeee',
      '--app-tc-tab-count-bg': isDark ? 'rgba(255,255,255,0.1)' : '#eef0f3',
      '--app-tc-tab-count-active-bg': isDark ? 'rgba(59,184,164,0.22)' : '#e8f7f4',
      '--app-tc-tab-count-active-color': isDark ? '#3bb8a4' : '#2e8b6e',
      // ── Teleconsent Wizard modal ──────────────────────────────────────────
      '--tc-wiz-bg': isDark ? '#1e2328' : '#ffffff',
      '--tc-wiz-bg-2': isDark ? '#252b32' : '#f7fafc',
      '--tc-wiz-ink': isDark ? '#e8edf2' : '#1a2434',
      '--tc-wiz-ink-2': isDark ? '#c8d0da' : '#38404d',
      '--tc-wiz-ink-3': isDark ? '#8b95a3' : '#5c6776',
      '--tc-wiz-ink-4': isDark ? '#cbd5e0' : '#8b95a3',
      '--tc-wiz-line': isDark ? '#2e3740' : '#e2e8f0',
      '--tc-wiz-line-2': isDark ? '#3a4452' : '#cbd5e0',
      '--tc-wiz-accent': isDark ? '#0fa0ba' : '#0b7285',
      '--tc-wiz-accent-strong': isDark ? '#0b8aa0' : '#095969',
      '--tc-wiz-accent-soft': isDark ? 'rgba(15, 160, 186, 0.18)' : '#e6f4f6',
      '--tc-wiz-accent-focus': isDark ? 'rgba(15, 160, 186, 0.25)' : 'rgba(11, 114, 133, 0.15)',
      '--tc-wiz-accent-icon-sel': isDark ? 'rgba(15, 160, 186, 0.18)' : 'rgba(11, 114, 133, 0.12)',
      '--tc-wiz-danger': isDark ? '#e05555' : '#c03a3a',
      '--tc-wiz-badge-bg': isDark ? '#2d2200' : '#fff7e6',
      '--tc-wiz-badge-bd': isDark ? '#7a5a00' : '#f2c879',
      '--tc-wiz-badge-ink': isDark ? '#f0c060' : '#7a4f00',
      '--tc-wiz-custom-icon-bg': isDark ? 'rgba(46, 139, 110, 0.15)' : '#e8f7f4',
      '--tc-wiz-custom-icon-ink': isDark ? '#4ecaa0' : '#2e8b6e',
      '--tc-wiz-custom-icon-sel-bg': isDark ? 'rgba(46, 139, 110, 0.25)' : 'rgba(46, 139, 110, 0.15)',
      '--tc-wiz-disabled': isDark ? '#3a4452' : '#b7c2d0',
      '--tc-wiz-scrim': isDark ? 'rgba(0, 0, 0, 0.65)' : 'rgba(8, 14, 22, 0.48)',
      '--tc-wiz-shadow': isDark
        ? '0 24px 64px rgba(0, 0, 0, 0.55), 0 6px 20px rgba(0, 0, 0, 0.35)'
        : '0 24px 64px rgba(11, 29, 46, 0.28), 0 6px 20px rgba(11, 29, 46, 0.14)',
      '--tc-wiz-hover-border': isDark ? '#3a4452' : '#b7c2d0',
      '--tc-wiz-sec-hover-border': isDark ? '#4a5568' : '#94a3b8',
      // ─────────────────────────────────────────────────────────────────────
      '--app-snackbar-info-bg': 'var(--app-brand-primary)',
      '--app-snackbar-success-bg': isCalm || isNature ? '#56AA59' : '#43a047',
      '--app-snackbar-error-bg': isCalm || isNature ? '#D74545' : '#d32f2f',
      '--app-snackbar-warning-bg': isCalm || isNature ? '#FFA21A' : '#ff9800',
      '.SnackbarContent-root.SnackbarItem-variantInfo': {
        backgroundColor: 'var(--app-snackbar-info-bg) !important',
        color: '#FFF !important',
      },
      '.SnackbarContent-root.SnackbarItem-variantSuccess': {
        backgroundColor: 'var(--app-snackbar-success-bg) !important',
        color: '#FFF !important',
      },
      '.SnackbarContent-root.SnackbarItem-variantError': {
        backgroundColor: 'var(--app-snackbar-error-bg) !important',
        color: '#FFF !important',
      },
      '.SnackbarContent-root.SnackbarItem-variantWarning': {
        backgroundColor: 'var(--app-snackbar-warning-bg) !important',
        color: '#FFF !important',
      },
      '.SnackbarContent-root.SnackbarItem-variantSuccess .MuiSvgIcon-root, .SnackbarContent-root.SnackbarItem-variantInfo .MuiSvgIcon-root, .SnackbarContent-root.SnackbarItem-variantError .MuiSvgIcon-root, .SnackbarContent-root.SnackbarItem-variantWarning .MuiSvgIcon-root':
        {
          color: '#FFF !important',
        },
    },
  };
};

function AppInner() {
  const DismissAction = ({ id }: { id: SnackbarKey }) => {
    const { closeSnackbar } = useSnackbar();

    return (
      <Button
        style={{
          height: '100%',
          width: '100%',
          position: 'absolute',
          left: 0,
          top: 0,
        }}
        onClick={() => closeSnackbar(id)}
      />
    );
  };

  return (
    <SnackbarProvider
      maxSnack={3}
      preventDuplicate
      anchorOrigin={{ horizontal: 'center', vertical: 'top' }}
      action={(key) => <DismissAction id={key} />}
    >
      <CssBaseline />
      <Router />
    </SnackbarProvider>
  );
}

function App() {
  const { themePreference } = useThemePreference();

  useEffect(() => {
    const root = document.documentElement;
    if (!root) {
      return;
    }

    const updateScrollbarCompensation = () => {
      const scrollbarWidth = Math.max(0, window.innerWidth - root.clientWidth);
      const compensation = scrollbarWidth > 0 ? `${scrollbarWidth / 2}px` : '0px';
      root.style.setProperty('--app-scrollbar-compensation', compensation);
    };

    updateScrollbarCompensation();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollbarCompensation();
    });

    resizeObserver.observe(root);
    resizeObserver.observe(document.body);
    window.addEventListener('resize', updateScrollbarCompensation);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollbarCompensation);
    };
  }, []);

  const theme = useMemo(() => getTheme(themePreference), [themePreference]);

  return (
    <Provider store={store}>
      <StyledEngineProvider injectFirst>
        <ThemeProvider theme={theme}>
          <CssBaseline />

          <GlobalStyles styles={getGlobalStyleVariables(theme)} />
          <AppInner />
        </ThemeProvider>
      </StyledEngineProvider>
    </Provider>
  );
}

export default App;
